import { useCallback, useEffect, useRef, useState } from "react";

import { queryNewsDetail } from "@api/news";

/**
 * 资讯详情取数。
 *
 * 进入详情 / id 变化时请求 `/api/news/detail?id=...`：
 * - 成功且 result.article 存在 → detail = result；
 * - success=false / article 缺失 → error=true，errorInfo 带业务错误码；
 * - 网络失败 → error=true，errorInfo="network"；
 * - 切详情 / 卸载时丢弃在途响应。
 *
 * 只负责接口与状态，不渲染；retry 供后续错误态 UI 使用。
 */
export default function useNewsDetail(id) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(Boolean(id));
  const [error, setError] = useState(false);
  const [errorInfo, setErrorInfo] = useState("");
  const requestSeqRef = useRef(0);

  const load = useCallback(() => {
    if (!id) {
      setDetail(null);
      setLoading(false);
      setError(false);
      setErrorInfo("");
      return;
    }

    const seq = requestSeqRef.current + 1;
    requestSeqRef.current = seq;
    setDetail(null);
    setLoading(true);
    setError(false);
    setErrorInfo("");

    queryNewsDetail(id)
      .then((res) => {
        if (seq !== requestSeqRef.current) return;

        if (!res?.success || !res.result?.article) {
          setDetail(null);
          setError(true);
          setErrorInfo(res?.info || res?.error || "");
          return;
        }

        setDetail(res.result);
        setError(false);
      })
      .catch(() => {
        if (seq !== requestSeqRef.current) return;
        setDetail(null);
        setError(true);
        setErrorInfo("network");
      })
      .finally(() => {
        if (seq === requestSeqRef.current) setLoading(false);
      });
  }, [id]);

  useEffect(() => {
    load();

    return () => {
      /* 切详情 / 卸载：作废这条链路上的在途响应 */
      requestSeqRef.current += 1;
    };
  }, [load]);

  return { detail, loading, error, errorInfo, retry: load };
}
